import { useState } from 'react'
import { message as $message } from 'antd'
import { queryDiscountedProductInformation } from '@/services/breakPrice'
import { createAPI } from '@/utils/modal'
import BreakPriceDrawer from '@/modules/distribute/components/BreakPriceDrawer'

export interface ItemType {
    // 商品skuid
    skuId: number
    // 商品名称
    skuName: string
    // 最低控价
    regulatePriceMin: number
    // 最高控价
    regulatePriceMax: number
    // 最小实付金额
    paidAmountMin: number
    // 最大实付金额
    paidAmountMax: number
    // 最小破价金额
    breakPriceMin: number
    // 最大破价金额
    breakPriceMax: number
    // 订单数
    orderCnt: number
}

interface QueryType {
    day: number
    co: {
        key: string
        label: string
    }
}

interface PaginationType {
    current: number
    pageSize: number
}

interface ShowDrawerProps {
    defaultQuery: QueryType
}

// 【分销商破价明细】抽屉
export interface BreakPriceDrawerType {
    // 列表
    list: ItemType[]
    // 列表Loading
    loading: boolean
    // 分页
    pagination: PaginationType
    // 列表总条数
    total: number
    // 筛选项
    query: QueryType
    // 根据筛选项搜索
    search: (_?: QueryType) => void
    // 设置筛选项
    setQuery: (_: QueryType) => void
    // 设置分页
    setPaginationWithFetch: (_: PaginationType) => void
    // 打开弹窗
    showDrawer: (_: ShowDrawerProps) => void
}

const initQuery = {
    day: 30,
    co: {
        key: '',
        label: ''
    }
}

const initPagination = {
    current: 1,
    pageSize: 20,
    total: 0
}

const useBreakPriceDrawer: () => BreakPriceDrawerType = () => {
    const [query, setQuery] = useState<QueryType>(initQuery)
    const [pagination, setPagination] = useState<PaginationType>(initPagination)
    const [total, setTotal] = useState<number>(0)
    const [list, setList] = useState<ItemType[]>([])
    const [loading, setLoading] = useState<boolean>(false)

    const fetch = ({ query, pagination }: { query: QueryType; pagination: PaginationType }) => {
        setLoading(true)
        const dataDayRangeMap = {
            1: 'TODAY',
            3: 'NEARLY_THREE_DAY',
            7: 'NEARLY_SEVEN_DAY',
            15: 'NEARLY_FIFTEEN_DAY',
            30: 'NEARLY_THIRTY_DAY'
        }
        queryDiscountedProductInformation({
            channelCoId: query.co.key,
            dataDayRange: dataDayRangeMap[query.day],
            pageNum: pagination.current,
            pageSize: pagination.pageSize
        })
            .then(response => {
                const { message, data, success, total } = response
                if (!success) {
                    $message.error(message)
                    return
                }
                setList(data?.list || [])
                setTotal(total)
            })
            .finally(() => setLoading(false))
    }

    const search = (newQuery?: QueryType) => {
        newQuery && setQuery(newQuery)
        setPagination(initPagination)
        fetch({ pagination: initPagination, query: newQuery || query })
    }

    const setPaginationWithFetch = (newPagination: PaginationType) => {
        setPagination(newPagination)
        fetch({ pagination: newPagination, query })
    }

    const showDrawer = ({ defaultQuery = initQuery }: ShowDrawerProps) => {
        setQuery(defaultQuery)
        setPagination(initPagination)
        setTotal(0)
        setList([])
        setLoading(false)
        createAPI(BreakPriceDrawer)
        search(defaultQuery)
    }

    return {
        list,
        loading,
        pagination,
        total,
        query,
        search,
        setQuery,
        setPaginationWithFetch,
        showDrawer
    }
}

export default useBreakPriceDrawer
